import { Component, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import {
  AuthorizationService,
  ErrorHandlerService,
  GlobalEventBusService,
  hd,
  TicketService,
  XDeviceService,
} from '@nx-fe/services';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  ActivityTypeEnum,
  IDeviceModel,
  INewAssociatedManagedDeviceModel,
  INewAssociatedUnmanagedDeviceModel,
  NoteActivity,
  Ticket,
} from '../../ticket.model';
import { TicketHeaderComponent } from '../ticket-header/ticket-header.component';
import { TicketDetailsDescriptionComponent } from '../ticket-details-description/ticket-details-description.component';
import { GlobalEvents } from 'sspapp/core/global-event-constants';
import { ActivityService } from '../services/activity.service';
import { TicketContextService } from '../../services/ticket-context.service';
import { DirtyComponent } from 'sspapp/services/can-deactivate-guard.service';
import { TicketPageService } from 'app/modules/ticket/ticket-page.service';
import { SspRestApiService } from 'sspapp/services/sspRestApiService';

type IError = hd.IError;

@Component({
  templateUrl: './ticket-content.component.html',
  styleUrls: ['./ticket-content.component.scss'],
})
export class TicketContentComponent implements OnInit, OnDestroy, DirtyComponent {
  public id: string;
  public ticket: Ticket;

  @ViewChild('headerTicket', { static: false })
  headerTicket: TicketHeaderComponent;

  @ViewChild('descriptionTicket', { static: false })
  descriptionTicket: TicketDetailsDescriptionComponent;

  private unsubscribe$ = new Subject<void>();
  private isSubmit = false;

  constructor(
    public ticketContextService: TicketContextService,
    public ticketPageService: TicketPageService,
    private route: ActivatedRoute,
    private location: Location,
    private sspRestApiService: SspRestApiService,
    private ticketService: TicketService,
    private errorHandlerService: ErrorHandlerService,
    private globalEventBusService: GlobalEventBusService,
    private authorizationService: AuthorizationService,
    private xDeviceService: XDeviceService,
    private activityService: ActivityService
  ) {}

  canDeactivate() {
    if (this.isSubmit) {
      this.isSubmit = false;
      return false;
    }

    if (this.isNew()) {
      if (
        (this.ticket.title && this.ticket.title.length > 0) ||
        (this.ticket.description && this.ticket.description.length > 0) ||
        (this.headerTicket && this.headerTicket.isGroupChange) ||
        (this.ticketContextService.activities && this.ticketContextService.activities.length > 0) ||
        (this.ticketContextService.devicesToAssociate && this.ticketContextService.devicesToAssociate.length > 0)
      ) {
        return true;
      }
    }

    return false;
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnloadHandler(event) {
    event = event || window.event;
    if (this.canDeactivate()) {
      // Modern Browser doesn't allow to customize message - but try to show it either way
      const message = this.deactivateMessage();
      if (event) {
        event.preventDefault();
        event.returnValue = message;
        return message;
      }
    }

    return undefined;
  }

  deactivateMessage() {
    return 'ticket_Edit_Confirm_Discard_Message';
  }

  ngOnInit(): void {
    this.init();
  }

  ngOnDestroy(): void {
    this.ticketPageService.destroy();
    if (this.unsubscribe$) {
      this.unsubscribe$.next();
      this.unsubscribe$.complete();
    }
  }

  private init = () => {
    this.route.params.pipe(takeUntil(this.unsubscribe$)).subscribe(params => {
      if (!params['submit']) {
        this.ticketContextService.resetTicketContext();
      }
      this.id = params['id'];
      if (!!this.id) {
        this.sspRestApiService.getTicket(this.id).subscribe((ticket: Ticket) => {
          if (!!ticket) {
            this.ticket = ticket;
            this.ticketContextService.ticket = ticket;
            this.ticketPageService.initialize(ticket as any);

            if (this.ticket.activities) {
              this.ticket.activities.forEach(activity => {
                this.activityService.patchActivity(activity);
              });
              this.ticketContextService.activities = ticket.activities as any[];
            }
            if (
              !this.ticketContextService.activities.some((noteActivity: NoteActivity) => {
                return (
                  noteActivity &&
                  noteActivity.activityTypeId == ActivityTypeEnum.note &&
                  noteActivity.details.isResolution
                );
              })
            )
              if (this.ticket.devices && this.ticket.devices.length > 0) {
                this.xDeviceService
                  .getDevices(this.ticket.devices.map(device => device.deviceId).toString())
                  .toPromise()
                  .then(
                    xDevices => (this.ticketContextService.associatedDevices = xDevices as undefined as IDeviceModel[])
                  );
              }
          }
        });
      } else {
        const reporterPublicId = this.authorizationService.getUserId();
        this.ticket = {
          status: 'open',
          reportedMethod: 'selfservice',
          reporterPublicId,
          title: '',
          description: '',
        } as Ticket;
        this.ticketContextService.ticket = this.ticket;
      }
    });
  };

  /** Whether the ticket associated with this instance is new */
  public isNew = () => {
    if (
      (this.ticketContextService.ticket && this.ticketContextService.ticket.ticketId) ||
      this.route.params['ticketId']
    ) {
      return false;
    }
    return true;
  };
  public ticketExists = () => {
    if (this.ticket && !this.ticket.isDeleted) {
      return true;
    }
    return false;
  };

  /** Deletes the attachment and associated activity */
  public delete = (attachmentActivity: hd.IActivityModel): void => {
    this.ticketContextService.activities = this.ticketContextService.activities.filter(
      activity => (activity as any) != attachmentActivity
    );
  };

  public cancelSubmit = () => {
    this.location.back();
  };

  public submitIncident = () => {
    this.isSubmit = true;
    //validation
    this.globalEventBusService.broadcast(GlobalEvents.validateTicket, {});
    if (!(this.headerTicket.isValid() && this.descriptionTicket.isValid())) {
      console.warn('Ticket form is not valid');
      return;
    }

    const ticket = this.ticket;
    this.ticketContextService.ticket = this.ticket;

    let promises: ng.IPromise<any>[] = [];

    this.ticketService.createSppTicket(ticket as any).subscribe(result => {
      this.ticketContextService.ticket.ticketId = result.ticketId;

      //Save devices
      for (const associateDevice of this.ticketContextService.devicesToAssociate) {
        if ((associateDevice as INewAssociatedManagedDeviceModel).deviceId) {
          promises.push(this.associateDeviceWithTicket(associateDevice as INewAssociatedManagedDeviceModel));
        } else {
          if ((associateDevice as INewAssociatedUnmanagedDeviceModel).deviceName) {
            promises.push(this.saveUnmanagedDevice(associateDevice as INewAssociatedUnmanagedDeviceModel));
          }
        }
      }

      const resolutionActivity = {
        activityId: undefined,
        activityType: ActivityTypeEnum[1],
        noteText: '',
        isPrivate: false,
        isResolution: true,
      };

      //Add resolution activity
      this.ticketService
        .saveActivity((<unknown>resolutionActivity) as hd.IActivityModel, this.ticketContextService.ticket.ticketId)
        .subscribe(() => {
          Promise.all(promises).finally(() => {
            //Save attachments
            this.globalEventBusService.broadcast(GlobalEvents.submitTicket, this.ticketContextService.ticket);
          });
        });
    });
  };

  private saveUnmanagedDevice = (device: INewAssociatedUnmanagedDeviceModel): ng.IPromise<any> => {
    return new Promise<any>((resolve, reject) => {
      this.xDeviceService
        .createUnmanagedDevice({
          deviceName: device.deviceName.trim(),
        } as hd.IUnmanagedDeviceModel)
        .subscribe(
          result => {
            const deviceId = result.deviceId;
            this.ticketService
              .createAssociatedDevice({
                ticketId: this.ticket.ticketId.toString(),
                deviceId,
                deviceAssociationId: deviceId,
              } as unknown as hd.IAssociatedDeviceModel)
              .subscribe(
                () => resolve(result),
                error => reject(error)
              );
          },
          error => reject(error)
        );
    });
  };

  private associateDeviceWithTicket = (device: INewAssociatedManagedDeviceModel): ng.IPromise<any> => {
    const newAssociation = {
      ticketId: this.ticket.ticketId,
      deviceId: device.deviceId,
      deviceAssociationId: device.deviceAssociationId,
    } as hd.IAssociatedDeviceModel;

    return new Promise<any>((resolve, reject) => {
      this.ticketService
        .createAssociatedDevice(newAssociation)
        .toPromise()
        .then(
          resp => {
            if (resp < 0) {
              const err: IError = { error_description: 'Failed to add the device' };
              this.errorHandlerService.setError(err);
            } else {
              // Associate the device with the ticket
              this.xDeviceService
                .getDevice(newAssociation.deviceId)
                .toPromise()
                .then(
                  xDevice => {
                    this.ticketContextService.associatedDevices.push(xDevice as undefined as IDeviceModel);
                    resolve(newAssociation.deviceId);
                  },
                  error => {
                    reject(error);
                  }
                );
            }
          },
          error => {
            reject(error);
          }
        );
    });
  };
}
